import React, { useEffect, useState, useMemo, useCallback, memo } from 'react';
import { isEqual } from 'lodash';
import SideDrawer from '../../../SideDrawer/SideDrawer';
import DataTableFilterDrawerHeader from './components/DataTableFilterDrawerHeader';
import DataTableFilterDrawerFooter from './components/DataTableFilterDrawerFooter';
import { DataTableColumn, FilterRendererMap, FilterType } from '../../DataTable.types';
import useFilterRenderers from '../../useFilterRenderers';
import styles from './DataTableFilterDrawer.module.scss';
import { useSelector } from 'react-redux';
import useDynamicFilterOptions from '../../useDynamicFilterOptions';
import LoadingSpinner from '../../../LoadingSpinner/LoadingSpinner';

const isRangeLikeFilterType = (ft: FilterType | undefined) => ft === FilterType.RANGE || ft === FilterType.DATE_RANGE;

const emptyValueForFilterType = (ft: FilterType | undefined): any => {
    return isRangeLikeFilterType(ft) ? null : [];
};

interface DataTableFilterDrawerProps {
    open: boolean;
    onClose: () => void;
    columns: DataTableColumn[];
    onApply: (filters: Record<string, any>) => void;
    width?: string;
    title?: string;
    customFilterRenderers?: FilterRendererMap;
    filterSourceKey: string;
}

const DataTableFilterDrawer: React.FC<DataTableFilterDrawerProps> = ({
    open,
    onClose,
    columns = [],
    onApply,
    width = '40%',
    title = 'Filters',
    customFilterRenderers,
    filterSourceKey,
}) => {
    const filterRenderers = useFilterRenderers(customFilterRenderers);
    const appliedFiltersFromStore = useSelector((state: any) => state.investor.customFilterDrawer.appliedFilters[filterSourceKey]);
    const { filterOptions, isLoading } = useDynamicFilterOptions(filterSourceKey, columns);

    // Memoize appliedFilters to prevent unnecessary re-renders
    const appliedFilters = useMemo(() => {
        return appliedFiltersFromStore ?? {};
    }, [appliedFiltersFromStore]);

    /* Only those columns are filterable which have a filterConfig with enabled: true and a filterType */
    const filterableColumns = useMemo(() => {
        const cols = columns.filter((column) => column.filterConfig?.enabled && column.filterConfig?.filterType);
        // Deal View: show Probability range filter first (matches product UX)
        return [...cols].sort((a, b) => {
            if (a.field === 'dealProbability') return -1;
            if (b.field === 'dealProbability') return 1;
            return 0;
        });
    }, [columns]);

    /* Function to initialize tempFilters with proper default values */
    const initializeTempFilters = useCallback(() => {
        const initialFilters: Record<string, any> = {};

        filterableColumns.forEach((column) => {
            const ft = column?.filterConfig?.filterType;
            // @todo: remove this once the getOrgBasedUsers api is updated
            if (column?.field === 'organizationOwner') {
                // Map owners.owner back to organizationOwner for display
                initialFilters[column?.field] = appliedFilters['owners.owner'] || appliedFilters[column?.field] || [];
            } else if (isRangeLikeFilterType(ft)) {
                const stored = appliedFilters[column?.field];
                initialFilters[column?.field] = stored ?? null;
            } else {
                initialFilters[column?.field] = appliedFilters[column?.field] || [];
            }
        });

        return initialFilters;
    }, [filterableColumns, appliedFilters]);

    const [tempFilters, setTempFilters] = useState<Record<string, any>>(() => initializeTempFilters());
    const [isDrawerReady, setIsDrawerReady] = useState(false);

    useEffect(() => {
        if (open) {
            // Reset drawer ready state
            setIsDrawerReady(false);

            // Delay initialization to allow the background animation to complete smoothly
            const timer = setTimeout(() => {
                setTempFilters(initializeTempFilters());
                setIsDrawerReady(true);
            }, 1000); // Delay to match the 1000ms background animation

            return () => clearTimeout(timer);
        } else {
            setIsDrawerReady(false);
        }
    }, [open, initializeTempFilters]);

    const handleFilterChange = useCallback((field: string, value: any) => {
        setTempFilters((prev) => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const handleApply = useCallback(() => {
        // Transform organizationOwner field to owners.owner for backend filtering
        const transformedFilters = { ...tempFilters };

        if (transformedFilters.organizationOwner) {
            transformedFilters['owners.owner'] = transformedFilters.organizationOwner;
            delete transformedFilters.organizationOwner;
        }

        onApply(transformedFilters);
        onClose();
    }, [tempFilters, onApply, onClose]);

    const handleClearAll = useCallback(() => {
        const clearedFilters: Record<string, any> = {};
        filterableColumns?.forEach((column) => {
            const ft = column?.filterConfig?.filterType;
            clearedFilters[column?.field] = emptyValueForFilterType(ft);
        });
        setTempFilters(clearedFilters);
    }, [filterableColumns]);

    const appliedValueForColumn = useCallback(
        (column: DataTableColumn) => {
            if (column?.field === 'organizationOwner') {
                return appliedFilters['owners.owner'] ?? appliedFilters[column.field];
            }
            return appliedFilters[column.field];
        },
        [appliedFilters],
    );

    /* Function to check if there are any changes to the filters */
    const hasChanges = useMemo(() => {
        return filterableColumns.some((column) => {
            const field = column.field;
            const ft = column.filterConfig?.filterType;
            const tempVal = tempFilters[field];
            const appVal = appliedValueForColumn(column);

            if (isRangeLikeFilterType(ft)) {
                return !isEqual(tempVal ?? null, appVal ?? null);
            }

            const t = tempVal || [];
            const a = appVal || [];
            if (t.length !== a.length) {
                return true;
            }
            if (t.length > 0 && JSON.stringify([...t].sort()) !== JSON.stringify([...a].sort())) {
                return true;
            }
            return false;
        });
    }, [tempFilters, filterableColumns, appliedValueForColumn]);

    return (
        <>
            {open && (
                <SideDrawer width={width} onClose={onClose}>
                    <div className={styles.DrawerContainer}>
                        <div className={styles.DrawerHeader}>
                            <DataTableFilterDrawerHeader title={title} onClose={onClose} />
                        </div>

                        <div className={styles.DrawerContent}>
                            {!isDrawerReady || isLoading ? (
                                <div className={styles.LoadingContainer}>
                                    <LoadingSpinner color={'#6021b3'} height="32px" />
                                </div>
                            ) : (
                                <div className={styles.FiltersContainer}>
                                    {filterableColumns?.map((column) => {
                                        const FilterRenderer = filterRenderers[column?.filterConfig?.filterType ?? ''];

                                        if (!FilterRenderer) {
                                            console.warn(`No filter renderer found for filter type: ${column?.filterConfig?.filterType}`);
                                            return null;
                                        }

                                        // Get options from filterOptions or column.dropdownOptions
                                        const options =
                                            filterOptions?.[column?.field] ||
                                            (column?.dropdownOptions
                                                ? Object.values(column?.dropdownOptions)
                                                      .filter((option) => !column?.filterConfig?.excludedValues?.includes(option.value))
                                                      .map((option) => ({
                                                          label: option?.label || '',
                                                          value: option?.value || '',
                                                      }))
                                                : []);

                                        const ft = column?.filterConfig?.filterType;
                                        const tempVal = tempFilters[column?.field];
                                        const rangeDefault = isRangeLikeFilterType(ft) ? null : [];

                                        return (
                                            <FilterRenderer
                                                key={column?.field}
                                                column={column}
                                                value={tempVal !== undefined ? tempVal : rangeDefault}
                                                onChange={(value) => handleFilterChange(column?.field, value)}
                                                options={options}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <DataTableFilterDrawerFooter
                            onSave={handleApply}
                            onClearAll={handleClearAll}
                            saveDisabled={!hasChanges || !isDrawerReady || isLoading}
                            saveText="Apply"
                            cancelText="Clear All"
                        />
                    </div>
                </SideDrawer>
            )}
        </>
    );
};

export default memo(DataTableFilterDrawer);
